import React, { useMemo, useState } from 'react';
import twoDigitFormat from '../../../scripts/helpers/two.digit.format';

const DAYS_SHORT = [ 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс' ];

const MONTH_NAMES = [

  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',

];

function parseDdMmYyyy( value ) {

  if ( typeof value !== 'string' ) { return null; }

  const parts = value.split( '.' );

  if ( parts.length !== 3 ) { return null; }

  const day = Number( parts[ 0 ] );
  const month = Number( parts[ 1 ] );
  const year = Number( parts[ 2 ] );

  if ( !day || !month || !year ) { return null; }

  return new Date( year, month - 1, day );

}

function toDdMmYyyy( date ) {

  return `${ twoDigitFormat( date.getDate() ) }.${ twoDigitFormat( date.getMonth() + 1 ) }.${ date.getFullYear() }`;

}

function sameDay( a, b ) {

  if ( !a || !b ) { return false; }

  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

}

function dayTime( date ) {

  return new Date( date.getFullYear(), date.getMonth(), date.getDate() ).getTime();

}

function isInRange( date, start, end ) {

  if ( !start || !end ) { return false; }

  const time = dayTime( date );
  const from = Math.min( dayTime( start ), dayTime( end ) );
  const to = Math.max( dayTime( start ), dayTime( end ) );

  return time >= from && time <= to;

}

function buildMonthRows( viewDate ) {

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date( year, month, 1 );
  const daysInMonth = new Date( year, month + 1, 0 ).getDate();
  const mondayIndex = ( firstDay.getDay() + 6 ) % 7;

  const cells = [];

  for ( let i = 0; i < mondayIndex; i++ ) {

    const date = new Date( year, month, 1 - ( mondayIndex - i ) );
    cells.push({ date, outside: true });

  }

  for ( let day = 1; day <= daysInMonth; day++ ) {

    cells.push({ date: new Date( year, month, day ), outside: false });

  }

  while ( cells.length % 7 !== 0 || cells.length < 42 ) {

    const last = cells[ cells.length - 1 ].date;
    const date = new Date( last.getFullYear(), last.getMonth(), last.getDate() + 1 );
    cells.push({ date, outside: true });

  }

  const rows = [];

  for ( let i = 0; i < cells.length; i += 7 ) {

    rows.push( cells.slice( i, i + 7 ) );

  }

  return rows;

}

const Calendar = ({

  startDate,
  endDate,
  onSelectRange,
  onClose,

}) => {

  const [ viewDate, setViewDate ] = useState( () => parseDdMmYyyy( startDate ) || new Date() );
  const [ hoverDate, setHoverDate ] = useState( null );
  const [ pickingEnd, setPickingEnd ] = useState( false );
  const [ draftStart, setDraftStart ] = useState( null );

  const rows = useMemo( () => buildMonthRows( viewDate ), [ viewDate ] );

  const selectedStart = draftStart || parseDdMmYyyy( startDate );
  const selectedEnd = pickingEnd ? null : parseDdMmYyyy( endDate );

  const highlightEnd = pickingEnd && draftStart
    ? ( hoverDate || draftStart )
    : selectedEnd;

  function shiftMonth( delta ) {

    setViewDate( prev => new Date( prev.getFullYear(), prev.getMonth() + delta, 1 ) );

  }

  function shiftYear( delta ) {

    setViewDate( prev => new Date( prev.getFullYear() + delta, prev.getMonth(), 1 ) );

  }

  function handleDayClick( date ) {

    if ( !pickingEnd || !draftStart ) {

      setDraftStart( date );
      setPickingEnd( true );
      setHoverDate( date );
      return;

    }

    let nextStart = draftStart;
    let nextEnd = date;

    if ( dayTime( nextEnd ) < dayTime( nextStart ) ) {

      nextStart = date;
      nextEnd = draftStart;

    }

    setPickingEnd( false );
    setDraftStart( null );
    setHoverDate( null );
    onSelectRange( toDdMmYyyy( nextStart ), toDdMmYyyy( nextEnd ) );
    onClose();

  }

  return (

    <div className = "company-stats__calendar">

      <div className = "company-stats__calendar-nav">

        <div className = "company-stats__calendar-nav-group">

          <button
            type = "button"
            className = "company-stats__calendar-arrow"
            onClick = { () => shiftYear( -1 ) }
            aria-label = "Предыдущий год"
          >‹</button>

          <span>{ viewDate.getFullYear() }</span>

          <button
            type = "button"
            className = "company-stats__calendar-arrow"
            onClick = { () => shiftYear( 1 ) }
            aria-label = "Следующий год"
          >›</button>

        </div>

        <div className = "company-stats__calendar-nav-group">

          <button
            type = "button"
            className = "company-stats__calendar-arrow"
            onClick = { () => shiftMonth( -1 ) }
            aria-label = "Предыдущий месяц"
          >‹</button>

          <span className = "company-stats__calendar-month">
            { MONTH_NAMES[ viewDate.getMonth() ] }
          </span>

          <button
            type = "button"
            className = "company-stats__calendar-arrow"
            onClick = { () => shiftMonth( 1 ) }
            aria-label = "Следующий месяц"
          >›</button>

        </div>

      </div>

      <div className = "company-stats__calendar-weekdays">

        { DAYS_SHORT.map( day => (

          <span key = { day }>{ day }</span>

        )) }

      </div>

      <div className = "company-stats__calendar-grid">

        { rows.map( ( row, rowIndex ) => (

          <div key = { rowIndex } className = "company-stats__calendar-row">

            { row.map( cell => {

              const isStart = sameDay( cell.date, selectedStart );
              const isEnd = sameDay( cell.date, highlightEnd );
              const inRange = selectedStart && highlightEnd
                && isInRange( cell.date, selectedStart, highlightEnd );

              const className = [

                'company-stats__calendar-day',
                cell.outside ? 'company-stats__calendar-day--outside' : '',
                inRange ? 'company-stats__calendar-day--range' : '',
                isStart || isEnd ? 'company-stats__calendar-day--edge' : '',

              ].filter( Boolean ).join( ' ' );

              return (

                <button

                  key = { toDdMmYyyy( cell.date ) }
                  type = "button"
                  className = { className }
                  onClick = { () => handleDayClick( cell.date ) }
                  onMouseEnter = { () => pickingEnd && setHoverDate( cell.date ) }
                  onFocus = { () => pickingEnd && setHoverDate( cell.date ) }

                >

                  { cell.date.getDate() }

                </button>

              );

            }) }

          </div>

        )) }

      </div>

    </div>

  );

};

export default Calendar;
